// frontend/src/lib/businessStore.js
/**
 * Multi-Entity Business Store (localStorage)
 *
 * Standard tier:
 * - Uses single profile key only (aifine_business_profile_v1)
 *
 * Top tier:
 * - Stores business index + per-business payloads
 * - Maintains an "active business id"
 * - Always writes active business into aifine_config (compat shim)
 *
 * Design goals:
 * - No manual localStorage manipulation required; UI drives everything
 * - Backwards-compatible with existing single-profile storage
 */

export const KEYS = {
    // Single-business legacy profile
    SINGLE_PROFILE: "aifine_business_profile_v1",
  
    // Multi-entity index + payloads
    BUSINESS_INDEX: "aifine_business_index_v1", // [{ business_id, entity_name, updated_at_iso, jurisdictions, industry, niche }]
    BUSINESS_PAYLOAD_PREFIX: "aifine_business_payload_", // + <business_id>
  
    // Active business pointer
    ACTIVE_BUSINESS_ID: "aifine_active_business_id_v1",
  
    // Compatibility shim read by other pages
    CONFIG: "aifine_config",
  };
  
  const MAX_BUSINESSES = 50; // safety cap
  
  function safeParse(raw, fallback) {
    try {
      if (!raw) return fallback;
      return JSON.parse(raw);
    } catch {
      return fallback;
    }
  }
  
  function safeString(x, fallback = "") {
    if (x == null) return fallback;
    return String(x);
  }
  
  function nowIso() {
    return new Date().toISOString();
  }
  
  function makeId() {
    // deterministic enough for local; avoids extra deps
    return `biz_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }
  
  export function readBusinessIndex() {
    const arr = safeParse(localStorage.getItem(KEYS.BUSINESS_INDEX), []);
    return Array.isArray(arr) ? arr : [];
  }
  
  function writeBusinessIndex(arr) {
    const clean = Array.isArray(arr) ? arr.slice(0, MAX_BUSINESSES) : [];
    localStorage.setItem(KEYS.BUSINESS_INDEX, JSON.stringify(clean));
  }
  
  export function getActiveBusinessId() {
    const id = localStorage.getItem(KEYS.ACTIVE_BUSINESS_ID);
    return id ? String(id) : null;
  }
  
  export function setActiveBusinessId(id) {
    if (!id) return;
    localStorage.setItem(KEYS.ACTIVE_BUSINESS_ID, String(id));
  }
  
  export function readBusinessPayload(businessId) {
    if (!businessId) return null;
    const raw = localStorage.getItem(`${KEYS.BUSINESS_PAYLOAD_PREFIX}${businessId}`);
    const obj = safeParse(raw, null);
    return obj && typeof obj === "object" ? obj : null;
  }
  
  export function persistBusinessPayload(businessId, payload) {
    if (!businessId) return;
    localStorage.setItem(
      `${KEYS.BUSINESS_PAYLOAD_PREFIX}${businessId}`,
      JSON.stringify(payload || {})
    );
  }
  
  function buildIndexRowFromPayload(businessId, payload) {
    const entity = safeString(payload?.clientName || payload?.entity_name || "Not specified", "Not specified");
    const updatedAt = safeString(payload?.updated_at_iso || nowIso(), nowIso());
    const jurisdictions = Array.isArray(payload?.jurisdictions) ? payload.jurisdictions : [];
    const industry = safeString(payload?.industry || "");
    const niche = safeString(payload?.niche || "");
    return {
      business_id: String(businessId),
      entity_name: entity,
      updated_at_iso: updatedAt,
      jurisdictions,
      industry,
      niche,
    };
  }
  
  /**
   * Save (upsert) a business payload in Top-tier storage,
   * update index row,
   * set it as active,
   * and write CONFIG shim for the rest of the app.
   */
  export function upsertTopTierBusiness(payload, businessId = null) {
    const id = businessId ? String(businessId) : makeId();
  
    const cleanPayload = {
      ...(payload || {}),
      business_id: id,
      updated_at_iso: nowIso(),
    };
  
    persistBusinessPayload(id, cleanPayload);
  
    const prev = readBusinessIndex();
    const row = buildIndexRowFromPayload(id, cleanPayload);
  
    const without = prev.filter((r) => String(r?.business_id) !== id);
    const next = [row, ...without].slice(0, MAX_BUSINESSES);
    writeBusinessIndex(next);
  
    setActiveBusinessId(id);
  
    // Compatibility shim
    localStorage.setItem(KEYS.CONFIG, JSON.stringify(cleanPayload));
  
    return { businessId: id, index: next, payload: cleanPayload };
  }
  
  /**
   * Select an existing business as active:
   * - Loads payload
   * - Sets ACTIVE id
   * - Writes CONFIG shim
   */
  export function selectTopTierBusiness(businessId) {
    const id = String(businessId || "").trim();
    if (!id) return null;
  
    const payload = readBusinessPayload(id);
    if (!payload) return null;
  
    setActiveBusinessId(id);
    localStorage.setItem(KEYS.CONFIG, JSON.stringify(payload));
    return payload;
  }
  
  /**
   * Standard tier: save single profile and CONFIG shim.
   */
  export function saveSingleProfile(payload) {
    const cleanPayload = {
      ...(payload || {}),
      updated_at_iso: nowIso(),
    };
  
    localStorage.setItem(KEYS.SINGLE_PROFILE, JSON.stringify(cleanPayload));
    localStorage.setItem(KEYS.CONFIG, JSON.stringify(cleanPayload));
    return cleanPayload;
  }
  
  /**
   * Standard tier: read single profile; fallback to CONFIG.
   */
  export function readSingleProfile() {
    const p = safeParse(localStorage.getItem(KEYS.SINGLE_PROFILE), null);
    if (p && typeof p === "object") return p;
  
    const cfg = safeParse(localStorage.getItem(KEYS.CONFIG), null);
    return cfg && typeof cfg === "object" ? cfg : null;
  }